import React from 'react';

// Components
import { LoadingSpinner } from '@/views/components/common';
import { MenuToggle } from '@/views/components/buttons';
import { withErrorBoundary } from '@/views/components/errors';

// Libraries
import classNames from 'classnames';
import moment from 'moment';
import { Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';

// Types
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { formatMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';

interface SharedEmailItemProps {
  authorizeEmail: (email: string) => Promise<void>;
  email: string;
  isAuthorizing: boolean;
  isRemoving: boolean;
  removeEmail: (email: string, riskProfile: FormattedFullOmniRiskProfile) => void;
  riskProfile: FormattedFullOmniRiskProfile;
  sentAt: string;
}

const SharedEmailItem: React.FC<SharedEmailItemProps> = ({ authorizeEmail, email, isAuthorizing, isRemoving, riskProfile, sentAt, removeEmail }) => {
  const user = useSelector((state: ReduxState) => state.auth.user);

  const formattedDatetime = moment(sentAt).format(formatMessage('RiskProfileModal.SendQuestionnaire.LastSentDateFormat'));
  const isButtonDisabled = isAuthorizing || isRemoving;

  const getMenuItems = () =>
    [
      {
        value: formatMessage('RiskProfileModal.SendQuestionnaire.ResendQuestionnaire'),
        icon: 'icon-ds-email',
        onClick: () => authorizeEmail(email),
        isAllowed: () => userHasPermissionEnabled(user, PERMISSIONS.OMNI_RISK_PROFILE_SEND_EMAIL_TO_CONTACT),
      },
      {
        className: 'destructive',
        value: formatMessage('RiskProfileModal.SendQuestionnaire.RemoveAccess'),
        icon: 'icon-ds-remove-circle-outline',
        onClick: () => removeEmail(email, riskProfile),
        isAllowed: () => true,
      },
    ].filter(i => i.isAllowed());

  const getMenuOptions = () => {
    const options = getMenuItems().map(item => (
      <Dropdown.Item
        key={item.value}
        role="menuitem"
        as="button"
        className={classNames(item.className)}
        disabled={isButtonDisabled}
        onClick={() => item.onClick && item.onClick()}
      >
        <i className={item.icon} />
        <span>{item.value}</span>
      </Dropdown.Item>
    ));

    if (!options.length) {
      return null;
    }

    return <MenuToggle options={options} alignRight />;
  };

  return (
    <div className="shared-email-item">
      <div className="icon-container">
        <i className="icon icon-ds-person" />
      </div>
      <div className="info-container">
        <p className="email" data-testid="sharedEmailItem-email">
          {email}
        </p>
        <time>{formattedDatetime}</time>
      </div>

      {getMenuOptions()}

      {(isAuthorizing || isRemoving) && <LoadingSpinner />}
    </div>
  );
};

export default withErrorBoundary(SharedEmailItem);
